/**
 * BUSCA DE EMPRESA POR ID OU RAZÃO SOCIAL — substitui o `<select>` nativo da aba
 * Processos (pedido do usuário, 17/09).
 *
 * POR QUE SUBSTITUIR O `<select>`
 * -------------------------------
 * O select nativo só "busca" pelo prefixo do label enquanto o usuário digita rápido,
 * e o label é "Nome_1117" — então digitar o CÓDIGO nunca encontrava nada, porque o
 * código está no FIM do texto. Com ~1,6 mil empresas, achar uma pelo código era
 * rolar a lista inteira.
 *
 * ⚠️ HOMÔNIMOS (a razão de isto não ser um combobox trivial)
 * ----------------------------------------------------------
 * `codi_emp` repete entre cadastros — o código `715` aparece em 22 deles. Um campo
 * que "seleciona ao digitar o código exato" entregaria a empresa errada com cara de
 * acerto. Aqui a seleção é SEMPRE por clique numa linha específica da lista, e
 * quando duas linhas compartilham (nome, código) o rótulo ganha grupo/CNPJ para
 * distingui-las (`rotuloDesambiguado`). Nada é colapsado.
 *
 * O QUE NÃO MUDA
 * --------------
 * O valor selecionado alimenta EXATAMENTE o mesmo filtro de antes
 * (`filtros.empresa` via `aoEscolherEmpresa`), incluindo o auto-preenchimento do
 * regime. Este componente troca a forma de escolher, não o que a escolha faz — era
 * requisito explícito preservar o comportamento de filtro existente.
 *
 * ACESSIBILIDADE: combobox nativo em espírito — `<input>` + lista de `<button>`,
 * navegação por ↑/↓/Enter/Esc, `aria-expanded`/`aria-activedescendant`. Os botões
 * são elementos nativos de propósito (foco, Enter/Espaço e leitor de tela de graça).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  buscarEmpresas,
  contarEmpresas,
  rotuloDesambiguado,
  temHomonimos,
  temNomeProprio,
  type OpcaoEmpresa,
} from '../utils/buscaEmpresa';
import { C, FONTE_MONO, FONTE_TEXTO } from '../paleta';

interface Props {
  /** Todas as empresas selecionáveis (já no recorte de regime da página). */
  opcoes: OpcaoEmpresa[];
  /** codi_emp selecionado, ou null para "todas". */
  valor: string | null;
  /** Recebe o codi_emp escolhido (null = limpar) — alimenta o filtro de sempre. */
  onEscolher: (codiEmp: string | null) => void;
  /** Largura do campo; o resto do layout vem do pai. */
  largura?: string;
}

const MAX_LISTA = 50;

export const BuscaEmpresa: React.FC<Props> = ({ opcoes, valor, onEscolher, largura = '230px' }) => {
  const [aberto, setAberto] = useState(false);
  const [termo, setTermo] = useState('');
  const [indice, setIndice] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const selecionada = useMemo(() => opcoes.find(o => o.codiEmp === valor) ?? null, [opcoes, valor]);

  const resultados = useMemo(() => buscarEmpresas(opcoes, termo, MAX_LISTA), [opcoes, termo]);
  const homonimos = useMemo(() => temHomonimos(resultados), [resultados]);

  /*
    Os rótulos são calculados UMA vez por lista, não duas vezes por linha dentro do
    JSX. `rotuloDesambiguado` faz dois `filter` com `normalizar()` (que roda
    `normalize('NFD')` + regex) sobre a lista inteira: chamá-lo no render de cada
    linha dava ~20 mil normalizações por tecla, travando justamente o campo que o
    teto de 50 itens existe para manter fluido.
  */
  const rotulos = useMemo(
    () => resultados.map(op => rotuloDesambiguado(op, resultados)),
    [resultados],
  );

  /*
    Keys ESTÁVEIS e únicas sem usar o índice.

    `(codiEmp, nome, cnpj)` colide de verdade — o código 715 está em 22 cadastros e o
    CNPJ pode vir nulo dos dois lados. Duas linhas com a mesma key fariam o React
    reusar o nó errado ao reordenar (o que acontece a cada tecla), ligando o clique a
    uma empresa diferente da exibida. Aqui um sufixo de ocorrência desempata: a
    primeira "715|ACME|" vira `...#0`, a segunda `...#1`. A key segue a IDENTIDADE da
    linha, não a posição — reordenar não a muda.
  */
  const keys = useMemo(() => {
    const vistos = new Map<string, number>();
    return resultados.map(op => {
      const base = `${op.codiEmp}|${op.nome}|${op.cnpj ?? ''}`;
      const n = vistos.get(base) ?? 0;
      vistos.set(base, n + 1);
      return `${base}#${n}`;
    });
  }, [resultados]);

  // Quantas casaram ANTES do corte — a UI precisa dizer que há mais, senão o
  // usuário conclui que a empresa não existe quando ela só ficou fora do teto.
  //
  // `contarEmpresas` só CONTA (não ordena nem materializa a lista de ~1,6 mil),
  // porque o número é tudo o que a frase precisa.
  const totalCasadas = useMemo(() => contarEmpresas(opcoes, termo), [opcoes, termo]);

  // O índice destacado volta ao topo sempre que a LISTA muda — não só quando o termo
  // muda. `opcoes` troca de identidade a cada refetch do resumo, e nesse instante
  // `resultados` é reconstruído com o mesmo `termo`: se o índice sobrevivesse, ele
  // passaria a apontar para OUTRA empresa e o Enter selecionaria quem o usuário nunca
  // destacou — exatamente o risco de homônimo que este componente existe para evitar.
  useEffect(() => setIndice(0), [resultados]);

  // Fechar ao clicar fora — sem isso a lista fica aberta sobre o resto da tela.
  useEffect(() => {
    if (!aberto) return undefined;
    const aoClicarFora = (ev: MouseEvent) => {
      if (!wrapperRef.current?.contains(ev.target as Node)) {
        setAberto(false);
        setTermo('');
      }
    };
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [aberto]);

  const escolher = (codiEmp: string | null) => {
    onEscolher(codiEmp);
    setAberto(false);
    setTermo('');
  };

  const aoTeclar = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setAberto(true);
      setIndice(i => Math.min(i + 1, resultados.length - 1));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setIndice(i => Math.max(i - 1, 0));
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      // Enter escolhe a linha DESTACADA, nunca "a que casa o texto": com códigos
      // homônimos, casar por texto entregaria uma empresa que o usuário não viu.
      const alvo = resultados[indice];
      if (alvo) escolher(alvo.codiEmp);
    } else if (ev.key === 'Escape') {
      setAberto(false);
      setTermo('');
    }
  };

  // Aberto mostra o que o usuário digita; fechado, o rótulo da empresa escolhida.
  const rotuloSelecionado = selecionada ? rotuloDesambiguado(selecionada, opcoes) : '';
  const textoVisivel = aberto ? termo : rotuloSelecionado;

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: largura }}>
      <input
        type="text"
        role="combobox"
        aria-expanded={aberto}
        aria-controls="busca-empresa-lista"
        aria-autocomplete="list"
        aria-activedescendant={
          aberto && resultados[indice] ? `busca-empresa-op-${indice}` : undefined
        }
        value={textoVisivel}
        placeholder="Todas as empresas"
        title="Pesquise por código (id) ou razão social"
        onFocus={() => setAberto(true)}
        onChange={ev => {
          setTermo(ev.target.value);
          setAberto(true);
        }}
        onKeyDown={aoTeclar}
        style={{
          width: '100%',
          padding: '7px 26px 7px 12px',
          borderRadius: '8px',
          border: '1px solid oklch(0.9 0.006 160)',
          background: 'oklch(1 0 0)',
          fontFamily: FONTE_TEXTO,
          fontSize: '12.5px',
          fontWeight: 600,
          color: C.ink,
          boxSizing: 'border-box',
        }}
      />
      {valor && (
        <button
          type="button"
          // Visível SEMPRE que há empresa escolhida — antes era `valor && !aberto`, e
          // como o `onFocus` do input abre a lista, o × desaparecia no exato momento
          // em que o usuário ia clicá-lo. Era UI morta em todo estado alcançável.
          onMouseDown={ev => ev.preventDefault()}
          onClick={() => escolher(null)}
          title="Limpar a empresa selecionada"
          aria-label="Limpar a empresa selecionada"
          style={{
            position: 'absolute',
            right: '6px',
            top: '50%',
            transform: 'translateY(-50%)',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: '13px',
            lineHeight: 1,
            color: 'oklch(0.5 0.015 165)',
            padding: '2px 4px',
          }}
        >
          ×
        </button>
      )}
      {!valor && !aberto && (
        <span
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            fontSize: '10px',
            color: 'oklch(0.5 0.015 165)',
          }}
        >
          ⌄
        </span>
      )}

      {aberto && (
        <div
          id="busca-empresa-lista"
          role="listbox"
          style={{
            position: 'absolute',
            zIndex: 40,
            top: 'calc(100% + 4px)',
            left: 0,
            // LARGURA (25/09, pedido do dono): "aumente a espessura do modal
            // dropselect para conseguir aparecer o nome da empresa e o codigo
            // tudo no modal". Com 300px a razao social longa era cortada por
            // reticencias e o codigo, na coluna da direita, saia da area
            // visivel — a lista mostrava menos informacao que o filtro precisa.
            //
            // `min()` com `92vw` para nao estourar a viewport em tela estreita:
            // um dropdown mais largo que a janela criaria rolagem horizontal na
            // pagina inteira, que e' justamente o que aparecia no print.
            width: 'min(92vw, max(100%, 460px))',
            // ESTOURO DE 18px (28/09, medido no DOM de produção): a página não tem
            // reset global de `box-sizing`, então cada botão de linha era
            // content-box — `width: 100%` = 460px MAIS 9px de padding de cada lado
            // = 478px dentro de uma área de 460px (scrollWidth 486 × clientWidth
            // 468). Os 18px que sobravam para fora eram justamente o fim do código,
            // e a sobra virava barra de rolagem horizontal. Alargar o container de
            // 300 para 460px (25/09) não resolveu porque alarga o estouro junto.
            //
            // `border-box` aqui e nos botões faz o padding caber DENTRO da largura;
            // `overflowX: 'hidden'` garante que um texto longo não traga a barra
            // de volta.
            boxSizing: 'border-box',
            maxHeight: '320px',
            overflowX: 'hidden',
            overflowY: 'auto',
            background: 'oklch(1 0 0)',
            border: '1px solid oklch(0.9 0.006 160)',
            borderRadius: '10px',
            boxShadow: '0 8px 24px oklch(0 0 0 / 0.1)',
            padding: '4px',
          }}
        >
          <button
            type="button"
            role="option"
            aria-selected={valor === null}
            onClick={() => escolher(null)}
            style={{
              display: 'block',
              width: '100%',
              // Sem isto o botão mede 478px numa área de 460px (28/09) — ver o
              // comentário do container.
              boxSizing: 'border-box',
              textAlign: 'left',
              padding: '7px 9px',
              border: 'none',
              borderRadius: '7px',
              background: valor === null ? 'oklch(0.96 0.01 165)' : 'transparent',
              cursor: 'pointer',
              font: 'inherit',
              fontSize: '12.5px',
              color: C.sub,
            }}
          >
            Todas as empresas
          </button>

          {homonimos && (
            // O aviso existe porque a colisão de `codi_emp` é real e frequente (o
            // código 715 está em 22 cadastros). Sem ele, duas linhas quase iguais
            // parecem um bug de duplicata em vez de duas empresas distintas.
            <div
              style={{
                padding: '6px 9px',
                fontSize: '11px',
                color: 'oklch(0.5 0.13 60)',
                textWrap: 'pretty',
              }}
            >
              Há empresas distintas com o mesmo código — o grupo/CNPJ aparece ao lado para
              diferenciá-las.
            </div>
          )}

          {resultados.map((op, i) => (
            <button
              key={keys[i]}
              id={`busca-empresa-op-${i}`}
              type="button"
              role="option"
              aria-selected={op.codiEmp === valor}
              onMouseEnter={() => setIndice(i)}
              onClick={() => escolher(op.codiEmp)}
              // O rótulo inteiro no hover: o desempate longo desce de linha, e aqui
              // ele fica legível de uma vez só.
              title={rotulos[i]}
              style={{
                display: 'grid',
                // A coluna da direita para em 70% da linha (28/09): com `auto` ela
                // assumia a largura do desempate inteiro e, com grupo de ~47
                // caracteres ou mais (área útil de ~417px, 6,6px por caractere),
                // empurrava o fim do texto — a raiz do CNPJ — para fora do botão.
                // O nome conserva pelo menos 30%.
                gridTemplateColumns: 'minmax(0, 1fr) fit-content(70%)',
                gap: '8px',
                alignItems: 'baseline',
                width: '100%',
                // Sem isto o botão mede 478px numa área de 460px e o fim do código
                // fica para fora (28/09) — ver o comentário do container.
                boxSizing: 'border-box',
                textAlign: 'left',
                padding: '7px 9px',
                border: 'none',
                borderRadius: '7px',
                background: i === indice ? 'oklch(0.96 0.01 165)' : 'transparent',
                cursor: 'pointer',
                font: 'inherit',
                fontSize: '12.5px',
                color: C.ink,
              }}
            >
              {/* Sem razão social cadastrada, a coluna da esquerda fica VAZIA em
                  vez de repetir o código que já está na direita (25/09). O
                  "103 … 103" do print não era duplicata de empresa: era a mesma
                  empresa com o fallback de `nomeEmpresa` impresso duas vezes. */}
              <span
                style={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  ...(temNomeProprio(op) ? {} : { color: C.sub, fontStyle: 'italic' }),
                }}
              >
                {temNomeProprio(op) ? op.nome : 'sem razão social cadastrada'}
              </span>
              {/* O código nunca quebra linha nem é cortado (28/09): quem cede espaço
                  é o NOME, que já tem `minWidth: 0` + reticências. */}
              <span
                style={{
                  fontFamily: FONTE_MONO,
                  fontSize: '11px',
                  color: C.sub,
                  whiteSpace: 'nowrap',
                  textAlign: 'right',
                }}
              >
                {op.codiEmp}
                {/* Desempate só quando o par (nome, código) colide — ver
                    `rotuloDesambiguado`. O rótulo vem pré-calculado (ver `rotulos`).

                    O desempate PODE quebrar linha, ao contrário do código (28/09):
                    o container não rola mais na horizontal, então um grupo longo
                    numa linha só seria cortado em silêncio — e o que se perde é o
                    fim, a raiz do CNPJ, deixando duas homônimas idênticas na tela. */}
                {rotulos[i].includes(' · ') && (
                  <span style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                    {` · ${rotulos[i].split(' · ').slice(1).join(' · ')}`}
                  </span>
                )}
              </span>
            </button>
          ))}

          {resultados.length === 0 && (
            <div style={{ padding: '10px 9px', fontSize: '12px', color: C.sub }}>
              Nenhuma empresa encontrada para “{termo}”.
            </div>
          )}

          {totalCasadas > resultados.length && (
            // Dizer que há mais é obrigatório: sem isso o usuário conclui que a
            // empresa não existe quando ela só ficou fora do teto da lista.
            <div
              style={{
                padding: '7px 9px',
                fontSize: '11px',
                color: C.sub,
                borderTop: '1px solid oklch(0.94 0.006 160)',
              }}
            >
              Mostrando {resultados.length} de {totalCasadas} — refine a busca.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
